import { memo, useEffect, useMemo, useRef, useState } from "react";
import { Box, Dialog } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import { useParams } from "react-router";
import { HStack } from "../../components/utils";
import GenericTableHeader from "../../shared/TableHeader";
import SearchInput from "../../components/shared/SearchInput";
import {
  ButtonSize,
  IconCTAButton,
  PrimaryCTAButton,
} from "../quickRound/CTAButtonComponents";
import { agConfigDashboardSideBar } from "../dashboardPage/AgGridConfig";
import { tableCellStyle } from "../../components/TableComponent";
import { CTADropdown } from "../../components/shared/Dropdown";
import {
  DirectorDetailDataModel,
  DirectorDetailModel,
} from "../../types/OnBoardDataModel";
import {
  DateOfAppointmentRender,
  DirectorNameRender,
  ResignationDateRender,
  TagsRender,
} from "./DirectorAgComponent";
import AlertDialog from "../../components/shared/AlertDialog";
import { useGetUserRoleGrants } from "../../queries/auth";
import { AddEditDirector } from "../../modals/AddEditDirectors";
import { useDeleteDirectorDetails } from "../../queries/directorDetails";
import { queryClient } from "../../queries";
import EmptyTableIllustration from "../reports/EmptyTableIllustration";
import { Headings } from "../../components/Headings";
import AddDirectorDocument from "../boardModule/DirectorDocumentsModule";
import { DocumentsPopup } from "../boardModule/DocPopup";
import { TableId } from "../../constants/TableIdConstants";
import { useTableStateManagement } from "../rounds/TableHook";
import CloudSetting from "../../components/shared/CloudIcon";
import { useAuthStore } from "../../store";

type Props = {
  data: DirectorDetailDataModel[];
};
export default function DirectorAgTable(props: Props) {
  const { cId, token } = useParams() as {
    cId: string;
    token: string;
  };

  const directorData = props.data;
  const gridApi = useRef<any>(null);
  const { data: userRoleData } = useGetUserRoleGrants();
  const [isColumnOpen, setIsColumnOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [isResigned, setIsResigned] = useState(false);
  const [filteredDirectorData, setFilteredDirectorData] = useState<any[]>(isResigned
    ? directorData?.filter((director) => director.isResigned === true)
    : directorData?.filter(
        (director) =>
          director.isResigned === undefined || director.isResigned === false
      ));

  const { mutate: deleteDirectorDetails, status } = useDeleteDirectorDetails();
  const [pageLoaded, setPageLoaded] = useState(false);
  const { companyId } = useAuthStore.getState();

  const {
    filterCondition,
    setFilterCondition,
    isSaving,
    uploadFilter,
    uploadColumn,
    tableState,
  } = useTableStateManagement(gridApi, `${TableId.directorTable}${companyId}${isResigned ? 'resigned' : 'active'}web`, pageLoaded);

  const handleCheckboxChange = () => {
    setIsResigned(!isResigned);
  };

  useEffect(() => {
    setFilteredDirectorData(isResigned
      ? directorData?.filter((director) => director.isResigned === true)
      : directorData?.filter(
          (director) =>
            director.isResigned === undefined || director.isResigned === false
        ));
  }, [directorData, isResigned]);

  const componentsRegistery = useMemo(
    () => ({
      directorName: memo(DirectorNameRender),
      dateOfAppointment: memo(DateOfAppointmentRender),
      resignationDate: memo(ResignationDateRender),
      tags: memo(TagsRender),
    }),
    []
  );

  const showNoRowsOverlay = () => (
    <EmptyTableIllustration
      text={`${
        isResigned
          ? "No Resigned Director details found"
          : "No Active Director details found"
      }`}
    />
  );

  const openToolPanel = (key: any) => {
    if (key === "columns") {
      if (gridApi) {
        if (!isColumnOpen) gridApi?.current?.api?.openToolPanel(key);
        else gridApi?.current?.api?.closeToolPanel();
        setIsColumnOpen((state) => !state);
        setIsFilterOpen(false);
      }
    } else if (key === "filters") {
      if (gridApi) {
        if (!isFilterOpen) {
          gridApi?.current?.api?.openToolPanel(key);
        } else gridApi?.current?.api?.closeToolPanel();
        setIsFilterOpen((state) => !state);
        setIsColumnOpen(false);
      }
    }
  };

  const [dialog, setDialog] = useState<{
    open: boolean;
    name?: "Edit" | "Delete" | "Add" | "Documents" | "";
    data?: any;
  }>({
    name: "",
    open: false,
  });

  const [filteredRowData, setFilteredRowData] = useState<any>([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const onAgGridFilterChanged = (grid: any) => {
    const filtersApplied = grid.api.isAnyFilterPresent();
    setIsFilterApplied(filtersApplied);
    const filteredData = grid.api
      .getModel()
      .rowsToDisplay.map((node: any) => node.data);
    setFilteredRowData(filteredData);
    // setPinnedBottomRowData(grid);
    uploadFilter();
  };

  useEffect(() => {
    const filterModel = gridApi.current?.api.getFilterModel();

    const filteredData = gridApi.current?.api
      .getModel()
      .rowsToDisplay.map((node: any) => node.data);

    setFilteredRowData(filteredData);
    gridApi.current?.api.setFilterModel(tableState?.filterState);
  }, [
    gridApi,
    tableState?.filterState,
    tableState?.columnState,
    filteredDirectorData,
    isResigned
  ]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      autoHeight: true,
      wrapHeaderText: true,
      suppressColumnVirtualisation: true,
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: ["reset"],
        maxNumConditions: 5,
      },
      minWidth: 150,
      filter: true,
      resizable: true,
      flex: 1,
    }),
    []
  );

  function setTableHeight() {
    if (
      filteredDirectorData.length === 1 ||
      filteredDirectorData.length === 0
    ) {
      return (filteredDirectorData.length + 4.78) * 80;
    } else if (filteredDirectorData.length === 2) {
      return (filteredDirectorData.length + 2.94) * 80;
    } else if (filteredDirectorData.length === 3) {
      return (filteredDirectorData.length + 3) * 80;
    } else if (filteredDirectorData.length === 4) {
      return (filteredDirectorData.length + 2.75) * 80;
    } else if (filteredDirectorData.length === 5) {
      return (filteredDirectorData.length + 2.7) * 80;
    } else if (filteredDirectorData.length === 6) {
      return (filteredDirectorData.length + 2.55) * 80;
    } else if (filteredDirectorData.length === 7) {
      return (filteredDirectorData.length + 2.35) * 80;
    } else if (filteredDirectorData.length === 8) {
      return (filteredDirectorData.length + 2.28) * 80;
    } else if (filteredDirectorData.length === 9) {
      return (filteredDirectorData.length + 2.22) * 80;
    } else {
      return 10 * 80;
    }
  }

  useEffect(() => {
    if (status === "success") {
      queryClient.invalidateQueries("getDirectorDetails");
      queryClient.refetchQueries("getDirectorDetails");
    }
  }, [status]);

  const gridOptions = {
    suppressRowVirtualisation: true,
    paginationAutoPageSize: false,
    suppressScrollOnNewData: true,
  };

  function deleteParticularDirector(directors?: DirectorDetailModel) {
    // setFieldValue(
    //   "directors",
    //   values?.directors?.filter((director) => director !== directors)
    // );
    toast("Director Deleted Successfully!", {
      type: "success",
      autoClose: 2000,
    });
    setDialog({ open: false });
  }

  function handleModifyAction(action: string, data: any, index: number) {
    if (action === "Edit") {
      setDialog({
        open: true,
        name: "Edit",
        data,
      });
    } else if (action === "Delete") {
      setDialog({
        open: true,
        name: "Delete",
        data,
      });
    } else if (action === "Documents") {
      setDialog({
        open: true,
        name: "Documents",
        data: data.id,
      });
    }
  }

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Director Name",
        field: "directorName",
        filter: "agSetColumnFilter",
        cellRenderer: "directorName",
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"],
        pinned: "left",
        initialWidth: 300,
        autoHeight: true,
        wrapText: true,
        suppressSizeToFit: true,
      },
      {
        headerName: "DIN",
        field: "din",
        filter: "agSetColumnFilter",
        cellRenderer: "din",
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"],
      },
      {
        headerName: "Type of Director",
        field: "typeOfDirector",
        filter: "agSetColumnFilter",
        cellRenderer: "typeOfDirector",
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"],
      },
      {
        headerName: "Email Id",
        field: "email",
        filter: "agSetColumnFilter",
        cellRenderer: "email",
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"],
      },
      {
        headerName: "Nationality",
        field: "nationality",
        filter: "agSetColumnFilter",
        cellRenderer: "nationality",
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"],
      },
      {
        headerName: "Resignation Date",
        field: "resignationDate",
        filter: "agSetColumnFilter",
        cellRenderer: "resignationDate",
        cellStyle: tableCellStyle,
        sortable: true,
        hide: !isResigned,
        menuTabs: ["filterMenuTab"],
      },
      {
        headerName: "Date of Appointment",
        field: "dateOfAppointment",
        filter: "agSetColumnFilter",
        cellRenderer: "dateOfAppointment",
        cellStyle: tableCellStyle,
        sortable: true,
        hide: true,
        menuTabs: ["filterMenuTab"],
      },
      {
        headerName: "Tags",
        field: "tags",
        filter: "agTextColumnFilter",
        cellRenderer: "tags",
        cellStyle: tableCellStyle,
        sortable: true,
        hide: true,
        menuTabs: ["filterMenuTab"],
      },
      {
        headerName: "",
        field: "actions",
        pinned: "right",
        hide: false,
        width: 80,
        maxWidth: 80,
        filter: false,
        menuTabs: [],
        colId: "action-column",
        suppressNavigable: true,
        suppressColumnsToolPanel: true,
        cellStyle: { "padding-top": "12px", "line-height": "18px" },
        resizable: false,
        sortable: false,
        cellRendererParams: ({ value }: { value: any }) => value?.props,
        cellRenderer: CTADropdown,
      },
    ],
    [isResigned]
  );

  const rowData = useMemo(() => {
    setPageLoaded(true);
    return filteredDirectorData?.map((directorData:any, index:number) => {
      const actions = (
        <CTADropdown
          actions={[
            {
              name: "Edit",
              disabled: cId !== undefined,
            },
            {
              name: "Delete",
              disabled: cId !== undefined,
            },
            {
              name: "Documents",
              disabled: false,
            },
          ]}
          onAction={(action) => {
            handleModifyAction(action.name, directorData, index);
          }}
        />
      );
      return {
        directorName: directorData.fullName,
        din: directorData.din,
        typeOfDirector: directorData.typeOfDirector,
        email: directorData.emailId,
        nationality: directorData.nationality,
        tags: directorData.tags,
        resignationDate:
          (directorData.isResigned &&
            directorData.transactions &&
            directorData.transactions[0]?.date) ||
          "",
        dateOfAppointment: directorData.dateOfAppointement,
        actions,
      };
    });
  }, [filteredDirectorData]);

  return (
    <>
      <div className="shadow-box border border-borderColor bg-white rounded-md px-2 py-6">
        <HStack className="flex-col lg:flex-row justify-start lg:justify-between  py-2 lg:items-center items-start">
          <div>
            <GenericTableHeader
              heading={"Director Details"}
              subHeading={`${filteredDirectorData?.length} Directors`}
            />
            <label className="themeSwitcherThree relative inline-flex cursor-pointer select-none items-start m-4 mt-2">
              <input
                type="checkbox"
                checked={isResigned}
                onChange={handleCheckboxChange}
                className="sr-only"
              />
              <div className="shadow-card flex h-[36px] w-[175px] items-center justify-center rounded-md bg-white border p-1">
                <span
                  className={`flex h-7 w-36 items-center justify-center rounded ${
                    !isResigned ? "bg-primary text-white" : "text-body-color"
                  }`}
                >
                  Active
                </span>
                <span
                  className={`flex h-7 w-36 items-center justify-center rounded ${
                    isResigned ? "bg-primary text-white" : "text-body-color"
                  }`}
                >
                  Resigned
                </span>
              </div>
            </label>
          </div>
          <HStack className="items-center justify-between w-full gap-4 px-8 py-2 lg:w-auto lg:justify-end">
            <CloudSetting
              tableId={`${TableId.directorTable}${companyId}${isResigned ? 'resigned' : 'active'}web`}
              isLoading={isSaving}
              filterCondition={filterCondition}
              setFilterCondition={setFilterCondition}
            />
            <SearchInput
              placeholder={`Search`}
              onChange={(e: any) => {
                gridApi.current.api.setQuickFilter(e.target.value);
              }}
            />
            {!cId && (
              <PrimaryCTAButton
                event-name="Delete Confirmation"
                className="px-4"
                onClick={() => {
                  setDialog({
                    open: true,
                    name: "Add",
                  });
                }}
                disabled={
                  userRoleData && userRoleData[0].role === "Admin Viewer"
                }
              >
                <Icon
                  icon="wpf:add-user"
                  width={18}
                  height={18}
                  className={`mr-1 border-transparent text-white bg-transparent rounded-md`}
                />
                Add
              </PrimaryCTAButton>
            )}
            <HStack className="gap-4">
              <IconCTAButton
                value={"Columns"}
                onClick={() => openToolPanel("columns")}
                buttonSize={ButtonSize.SMALL}
                iconName={"fluent:column-triple-edit-20-regular"}
                className={`px-4 font-medium items-center flex flex-row ${
                  isColumnOpen ? "text-orange-501" : "text-gray-400"
                }`}
              />
              <IconCTAButton
                value={"Filters"}
                buttonSize={ButtonSize.SMALL}
                onClick={() => openToolPanel("filters")}
                iconName={"material-symbols:filter-alt"}
                className={`px-4 font-medium items-center flex flex-row ${
                  isFilterOpen ? "text-orange-501" : "text-gray-400"
                }`}
              />
            </HStack>
          </HStack>
        </HStack>
        <Dialog open={dialog.open} maxWidth="md">
          {(dialog.name === "Edit" || dialog.name === "Add") && (
            <AddEditDirector
              director={dialog.data}
              onSecondaryAction={() => {
                setDialog({ open: false });
              }}
              mode={dialog.name}
            />
          )}
          {dialog.name === "Documents" && (
            <DocumentsPopup
              id={dialog?.data || ""}
              onPrimaryAction={() => {
                setDialog({
                  open: true,
                  name: "Documents",
                  data: dialog.data,
                });
              }}
              onSecondaryAction={() => {
                setDialog({
                  open: false,
                  name: "Documents",
                });
              }}
              type="director"
            />
          )}
          {dialog.name === "Delete" && (
            <AlertDialog
              onClose={() => setDialog({ open: false })}
              open={dialog.open}
              message={"Do you want to delete this Director Details?"}
              onPrimaryAction={() => {
                deleteDirectorDetails({
                  id: dialog.data.id,
                  companyId: dialog.data.companyId,
                });
                setDialog({ open: false });
              }}
              onSecondaryAction={() => setDialog({ open: false })}
              primaryActionText="Delete"
            />
          )}
        </Dialog>
        <HStack className="justify-between w-full">
          <Box
            style={{
              height: setTableHeight(),
            }}
            className="w-full h-full max-h-full overflow-x-auto ag-theme-material "
          >
            <AgGridReact
              sideBar={agConfigDashboardSideBar}
              onGridReady={(params) => {
                // Store the grid API referen
                gridApi.current = params;
              }}
              onColumnEverythingChanged={uploadColumn}
              onColumnResized={uploadColumn}
              onColumnMoved={uploadColumn}
              components={componentsRegistery}
              alwaysShowHorizontalScroll
              alwaysMultiSort
              animateRows={true}
              defaultColDef={defaultColDef}
              onFilterChanged={onAgGridFilterChanged}
              // onColumnEverythingChanged={uploadColumn}
              rowData={rowData}
              // onCellClicked={handleCellClick}
              columnDefs={columnDefs}
              pagination={false}
              suppressRowTransform={true}
              suppressCopyRowsToClipboard={true}
              suppressCopySingleCellRanges={true}
              suppressCellFocus={true}
              suppressMenuHide={false}
              // onColumnResized={uploadColumn}
              noRowsOverlayComponent={showNoRowsOverlay}
              tooltipShowDelay={1000}
              tooltipInteraction={true}
              rowClass={"border-t border-dashed"}
              gridOptions={gridOptions}
              overlayNoRowsTemplate={`<span style="padding: 6px; border: 2px solid #444; background: lightgoldenrodyellow; margin-top: 50px; border-radius: 5px;">${
                isResigned
                  ? "No Resigned Director details found"
                  : "No ACtive Director details found"
              }</span>`}
              getRowStyle={(params) => {
                if (params.rowIndex % 2 === 0) {
                  return { background: "#f8f8f8" };
                } else {
                  return { background: "#ffffff" };
                }
              }}
            ></AgGridReact>
          </Box>
        </HStack>
      </div>
    </>
  );
}
